import {
    Button,
    Card,
    Col,
    ConfigProvider,
    Input,
    message,
    Popconfirm,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Tooltip
} from "antd";
import {DeleteOutlined, EditOutlined, ReloadOutlined, SearchOutlined} from "@ant-design/icons";
import React, {useEffect, useMemo, useState} from "react";
import {expenseCategoriesApi} from "./ExpensesSettings";
import style from './subcomponents/styles/expenses.periodic.module.css'
import EuroFormatter from "./_helpers/EuroFormatter";
import AddPeriodicExpenseModal from "./subcomponents/add_periodic_expense_modal/AddPeriodicExpenseModal";
import {instance} from "../../../API/API";
import ruRU from "antd/locale/ru_RU";
import dayjs from "dayjs";
import {formatDaysOfWeek, formatMonths} from "./_helpers/formatDaysOfWeek";


const ExpensesPeriodic = props => {
    const [modalVisible, setModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [categories, setCategories] = useState(null);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);

    // Фильтры
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');
    const [scheduleTypeFilter, setScheduleTypeFilter] = useState('all');

    // API endpoints
    const api = {
        getAll: () => instance.get(`/finance_04122025/get_periodic_expenses.php`).then(res => res.data),
        create: (data) => instance.post(`/finance_04122025/create_periodic_expense.php`, data).then(res => res.data),
        update: (id, data) => instance.put(`/finance_04122025/update_periodic_expense.php?id=${id}`, data).then(res => res.data),
        delete: (id) => instance.delete(`/finance_04122025/delete_periodic_expense.php?id=${id}`).then(res => res.data)
    }

    const getCategories = async () => {
        try {
            const data = await expenseCategoriesApi.getAll();
            setCategories(data.data);
        } catch (error) {
            message.error('Ошибка загрузки категорий');
        }
    };

    const getExpenses = async () => {
        setLoading(true);
        try {
            const response = await api.getAll();
            if (response.success) {
                setExpenses(response.data.sort((a, b) => a.id - b.id));
            } else {
                message.error(response.error || 'Ошибка загрузки расходов');
            }
        } catch (error) {
            message.error('Ошибка подключения к серверу');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getCategories();
        getExpenses();
    }, []);

    // Фильтрация данных
    const filteredData = useMemo(() => {
        return expenses.filter(item => {
            // Поиск по названию и описанию
            const matchesSearch = searchText === '' ||
                item.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                item.description?.toLowerCase().includes(searchText.toLowerCase());

            // Фильтр по статусу (активен/неактивен)
            const matchesStatus = statusFilter === 'all' ||
                (statusFilter === 'active' && item.is_active) ||
                (statusFilter === 'inactive' && !item.is_active);

            // Фильтр по категории
            const matchesCategory = categoryFilter === 'all' ||
                String(item.category_id) === String(categoryFilter);

            // Фильтр по типу оплаты
            const matchesPaymentType = paymentTypeFilter === 'all' ||
                (paymentTypeFilter === 'cash' && item.payment_type === 1) ||
                (paymentTypeFilter === 'non_cash' && item.payment_type === 2);

            // Фильтр по типу расписания
            const matchesScheduleType = scheduleTypeFilter === 'all' ||
                item.schedule_type === scheduleTypeFilter;

            return matchesSearch && matchesStatus && matchesCategory &&
                matchesPaymentType && matchesScheduleType;
        });
    }, [expenses, searchText, statusFilter, categoryFilter, paymentTypeFilter, scheduleTypeFilter]);

    const getCategoryNameById = (id) => {
        const category = categories ? categories.find(el => +el.id === +id) : null;
        const name = category ? category.name : '';
        const color = category ? category.color : '';
        return {name, color};
    };

    const handleDelete = async (record) => {
        try {
            const response = await api.delete(record.id);
            if (response.success) {
                message.success('Расход успешно удален');
                getExpenses();
            } else {
                message.error(response.error || 'Ошибка удаления');
            }
        } catch (error) {
            message.error('Ошибка подключения к серверу');
        }
    };

    const handleCreate = async (data) => {
        try {
            // Преобразуем данные для API
            const apiData = {
                name: data.name,
                description: data.description || '',
                category_id: parseInt(data.category_id),
                amount: parseFloat(data.amount),
                is_active: data.isActive !== undefined ? data.isActive : true,
                is_cash: data.isCash ? 1 : 2,
                tax_rate: data.isCash ? 0 : data.tax_rate,
                schedule_type: data.schedule_type || 'monthly',
                date_start: data.date_start ? data.date_start : dayjs().format('DD.MM.YYYY'),
                date_end: data.date_end ? data.date_end : null,
                time_of_day: data.time_of_day || '00:00',
                schedule_day: data.schedule_day || null,
                schedule_month: data.schedule_month || null,
                schedule_days_of_week: data.schedule_days_of_week ? data.schedule_days_of_week.join(',') : '',
            };

            const response = await api.create(apiData);
            if (response.success) {
                message.success('Расход успешно создан');
                setModalVisible(false);
                getExpenses();
            } else {
                message.error(response.error || 'Ошибка создания');
            }
        } catch (error) {
            console.error('Error creating expense:', error);
            message.error('Ошибка подключения к серверу');
        }
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        setEditModalVisible(true);
    };

    const handleUpdate = async (data) => {
        console.log(data);
        try {
            const apiData = {
                id: data.id,
                name: data.name,
                description: data.description || '',
                category_id: parseInt(data.category_id),
                amount: parseFloat(data.amount),
                is_active: data.is_active,
                payment_type: data.isCash ? 1 : 2,
                tax_rate: data.isCash ? 0 : data.tax_rate,
                schedule_type: data.schedule_type || 'monthly',
                date_start: data.date_start ? data.date_start : dayjs().format('DD.MM.YYYY'),
                date_end: data.date_end ? data.date_end : null,
                time_of_day: data.time_of_day || '00:00',
                schedule_day: data.schedule_day || null,
                schedule_month: data.schedule_month || null,
                schedule_days_of_week: data.schedule_days_of_week.join(',') || '',
            };

            const response = await api.update(editingRecord.id, apiData);
            if (response.success) {
                message.success('Расход успешно обновлен');
                setEditModalVisible(false);
                setEditingRecord(null);
                getExpenses();
            } else {
                message.error(response.error || 'Ошибка обновления');
            }
        } catch (error) {
            message.error('Ошибка подключения к серверу');
        }
    };

    // Обработчики фильтров
    const handleSearch = (value) => setSearchText(value);
    const handleStatusChange = (value) => setStatusFilter(value);
    const handleCategoryChange = (value) => setCategoryFilter(value);
    const handlePaymentTypeChange = (value) => setPaymentTypeFilter(value);
    const handleScheduleTypeChange = (value) => setScheduleTypeFilter(value);

    const handleResetFilters = () => {
        setSearchText('');
        setStatusFilter('all');
        setCategoryFilter('all');
        setPaymentTypeFilter('all');
        setScheduleTypeFilter('all');
    };

    const ScheduleView = ({record}) => {
        return (
            <div style={{fontSize: '12px'}}>
                <div className={style.date_start}>
                    <div>{record.date_start} / {record.date_end}</div>
                    <Tag variant={'outlined'} color={record.is_active ? '#0e8605' : '#ccc'}>
                        {getScheduleTypeLabel(record.schedule_type)}
                    </Tag>
                </div>
                <p><strong>Время списания:</strong><span
                    style={{color: '#c608cd'}}> {record.time_of_day || '00:00'}</span></p>
                {record.schedule_day && <p><strong>Интервал:</strong> каждое {record.schedule_day} число месяца</p>}
                {record.schedule_type === 'yearly' &&
                    <p><strong>Месяц:</strong> {formatMonths(record.schedule_month)}</p>}
                {record.schedule_type === 'weekly' && record.schedule_days_of_week.trim() !== '' && (
                    <p><strong>Дни недели:</strong> {formatDaysOfWeek(record.schedule_days_of_week)}</p>
                )}
            </div>
        )

    };

    const getScheduleTypeLabel = (type) => {
        const types = {
            daily: 'Ежедневно',
            weekly: 'Еженедельно',
            monthly: 'Ежемесячно',
            yearly: 'Ежегодно'
        };
        return types[type] || type;
    };

    const getPaymentTypeLabel = (payment_type) => {
        return payment_type === 1 ? 'Наличный' : 'Безналичный';
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            align: 'center',
            width: 50,
            render: (text, record) => <Tag color={'blue'}>{text}</Tag>
        },
        {
            title: 'Наименование',
            dataIndex: 'name',
            key: 'name',
            align: 'left',

            render: (text, record) => {
                const {name, color} = getCategoryNameById(record.category_id);

                return (
                    <div>
                        <div style={{fontWeight: 600}}>{text}</div>
                        <div style={{color: '#727272', fontSize: '12px'}}>{record.description}</div>
                        <span className={style.categoty_tag}>
                            <Tag variant={'outlined'} color={color} key={record.id}>
                                {name}
                            </Tag>
                        </span>
                    </div>
                )
            }
        },
        {
            title: 'Сумма',
            dataIndex: 'amount',
            key: 'amount',
            width: 150,
            render: (text, record) => {
                return (
                    <div style={{width: '100%'}}>
                        <span style={{fontSize: '16px', fontWeight: 600, color: '#444444'}}><EuroFormatter
                            amount={record.total_amount_with_tax}/></span>
                        {
                            record.payment_type !== 1 && (record.tax_rate !== "0.00" && +record.tax_rate !== 0) && (
                                <div style={{fontSize: '12px', color: '#888'}}>
                                    (в т.ч. НДС: <EuroFormatter
                                    amount={(record.total_amount_with_tax - (record.total_amount_with_tax / 1.21))}/>)
                                </div>
                            )
                        }
                    </div>
                )
            }
        },
        {
            title: 'Тип оплаты',
            dataIndex: 'payment_type',
            key: 'payment_type',
            width: 100,
            render: (text) => (
                <Tag color={text === 1 ? 'green' : 'blue'}>
                    {getPaymentTypeLabel(text)}
                </Tag>
            )
        },
        {
            title: 'Расписание',
            dataIndex: 'schedule_type',
            key: 'schedule_type',
            width: 300,
            render: (text, record) => <ScheduleView record={record}/>
        },
        {
            title: '',
            key: 'actions',
            align: 'center',
            width: 80,
            render: (text, record) => (
                <div className={style.container_icons}>
                    <Tooltip title="Редактировать">
                        <EditOutlined
                            style={{color: '#888888', fontSize: '16px', cursor: 'pointer', margin: '0 8px'}}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Удалить запись?"
                        description="Вы уверены, что хотите удалить эту запись?"
                        onConfirm={() => handleDelete(record)}
                        okText="Да"
                        cancelText="Нет"
                    >
                        <Tooltip title="Удалить">
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined/>}
                            />
                        </Tooltip>
                    </Popconfirm>
                </div>
            )
        }
    ];

    return (
        <div>
            <ConfigProvider locale={ruRU}>
                <div className={style.add_btn_cont}>
                    <Button type="primary" onClick={() => setModalVisible(true)}>
                        Добавить
                    </Button>
                </div>

                {/* Карточка с фильтрами по аналогии с SalaryStatementsTable */}
                <Card style={{marginBottom: 16}}>
                    <Space orientation="vertical" size="middle" style={{width: '100%', marginBottom: '16px'}}>
                        <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} md={6}>
                                <Input
                                    placeholder="Поиск по названию и описанию..."
                                    prefix={<SearchOutlined/>}
                                    value={searchText}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} md={4}>
                                <Select
                                    style={{width: '100%'}}
                                    placeholder="Статус"
                                    value={statusFilter}
                                    onChange={handleStatusChange}
                                    allowClear
                                >
                                    <Select.Option value="all">Все статусы</Select.Option>
                                    <Select.Option value="active">Активен</Select.Option>
                                    <Select.Option value="inactive">Не активен</Select.Option>
                                </Select>
                            </Col>
                            <Col xs={24} md={4}>
                                <Select
                                    style={{width: '100%'}}
                                    placeholder="Категория"
                                    value={categoryFilter}
                                    onChange={handleCategoryChange}
                                    allowClear
                                    loading={!categories}
                                >
                                    <Select.Option value="all">Все категории</Select.Option>
                                    {categories?.map(category => (
                                        <Select.Option key={category.id} value={category.id}>
                                            {category.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col xs={24} md={4}>
                                <Select
                                    style={{width: '100%'}}
                                    placeholder="Тип оплаты"
                                    value={paymentTypeFilter}
                                    onChange={handlePaymentTypeChange}
                                    allowClear
                                >
                                    <Select.Option value="all">Все типы</Select.Option>
                                    <Select.Option value="cash">Наличный</Select.Option>
                                    <Select.Option value="non_cash">Безналичный</Select.Option>
                                </Select>
                            </Col>
                            <Col xs={24} md={4}>
                                <Select
                                    style={{width: '100%'}}
                                    placeholder="Периодичность"
                                    value={scheduleTypeFilter}
                                    onChange={handleScheduleTypeChange}
                                    allowClear
                                >
                                    <Select.Option value="all">Все типы</Select.Option>
                                    <Select.Option value="daily">Ежедневно</Select.Option>
                                    <Select.Option value="weekly">Еженедельно</Select.Option>
                                    <Select.Option value="monthly">Ежемесячно</Select.Option>
                                    <Select.Option value="yearly">Ежегодно</Select.Option>
                                </Select>
                            </Col>
                            <Col xs={24} md={2}>
                                <Button
                                    icon={<ReloadOutlined/>}
                                    onClick={handleResetFilters}

                                >

                                </Button>
                            </Col>
                        </Row>

                        <Row gutter={[8, 8]}>
                            <Col>
                                <Tag color="processing">Всего записей: {expenses.length}</Tag>
                            </Col>
                            <Col>
                                <Tag color="success">Найдено: {filteredData.length}</Tag>
                            </Col>
                        </Row>
                    </Space>
                </Card>

                <Table
                    dataSource={filteredData}
                    columns={columns}
                    rowKey="id"
                    bordered={true}
                    size={'small'}
                    loading={loading}
                    pagination={false}
                />

                {/* Модальное окно создания */}
                <AddPeriodicExpenseModal
                    visible={modalVisible}
                    editMode={false}
                    onCancel={() => setModalVisible(false)}
                    onSave={handleCreate}
                    categories={categories || []}
                    title="Добавить периодический расход"
                />

                {/* Модальное окно редактирования */}
                {editingRecord && (
                    <AddPeriodicExpenseModal
                        visible={editModalVisible}
                        editMode={true}
                        onCancel={() => {
                            setEditModalVisible(false);
                            setEditingRecord(null);
                        }}
                        onSave={handleUpdate}
                        categories={categories || []}
                        title="Редактировать периодический расход"
                        initialValues={{
                            name: editingRecord.name,
                            description: editingRecord.description,
                            category: editingRecord.category_id,
                            amount: editingRecord.amount,
                            isActive: editingRecord.is_active,
                            isCash: editingRecord.payment_type === 1,
                            tax: editingRecord.tax_rate,
                            schedule: {
                                type: editingRecord.schedule_type,
                                dateOfStart: editingRecord.date_start,
                                dateOfEnd: editingRecord.date_end,
                                days: editingRecord.schedule_days || [],
                                daysOfWeek: editingRecord.schedule_days_of_week || [],
                                day: editingRecord.schedule_day,
                                month: editingRecord.schedule_month
                            },
                            time: editingRecord.time_of_day || '00:00'
                        }}
                    />
                )}
            </ ConfigProvider>
        </div>
    );
};

export default ExpensesPeriodic;